import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { fetchAPI } from './api';
import { SessionNote, YoutubeVod } from '../components/Notes';

export interface Note {
  note_id: string;
  seconds_from_start: number;
  content: string;
  labels: Record<string, string>;
  user_id: string;
  org_id?: string;
  version: number;
}

// Query keys
export const notesKeys = {
  all: ['notes'] as const,
  youtubeId: (youtubeId: string) => [...notesKeys.all, youtubeId] as const,
  session: (youtube_vods: YoutubeVod[]) => [...notesKeys.all, 'session', youtube_vods] as const,
};

// Fetch notes for a specific YouTube video
export const fetchNotesForVideo = async (youtubeId: string): Promise<Note[]> => {
  const response = await fetchAPI(`/notes/${youtubeId}`);
  return response.notes || [];
};

// Helper function to sort notes by seconds_from_start, then by note_id
export const sortNotes = (notes: Note[]): Note[] => {
  return notes.sort((a, b) => {
    if (a.seconds_from_start !== b.seconds_from_start) {
      return a.seconds_from_start - b.seconds_from_start;
    }
    return a.note_id.localeCompare(b.note_id);
  });
};

// Helper function to sort session notes by global_offset, then by note_id
export const sortSessionNotes = (notes: SessionNote[]): SessionNote[] => {
  return notes.sort((a, b) => {
    if (a.global_offset !== b.global_offset) {
      return a.global_offset - b.global_offset;
    }
    return a.note_id.localeCompare(b.note_id);
  });
};

// Convert notes to session notes with global offset
export const convertToSessionNotes = (youtube_vods: YoutubeVod[], allNotes: Record<string, Note[]>): SessionNote[] => {
  const sessionNotes: SessionNote[] = [];
  
  for (const vod of youtube_vods) {
    const notes = allNotes[vod.youtube_id] || [];
    const vodNotes: SessionNote[] = notes.map((note) => ({
      ...note,
      youtube_id: vod.youtube_id,
      global_offset: vod.seconds_from_session_start + note.seconds_from_start
    }));
    
    sessionNotes.push(...vodNotes);
  }
  
  // Sort notes by global offset, then by note_id for consistent ordering
  return sortSessionNotes(sessionNotes);
};

// Hook to fetch session notes for multiple YouTube videos
export const useSessionNotes = (youtube_vods: YoutubeVod[], enabled: boolean = true) => {
  // Use useQueries for dynamic queries
  const queries = useQueries({
    queries: youtube_vods.map(vod => ({
      queryKey: notesKeys.youtubeId(vod.youtube_id),
      queryFn: () => fetchNotesForVideo(vod.youtube_id),
      enabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }))
  });
  
  // Combine all results
  const isLoading = queries.some(query => query.isLoading);
  const isError = queries.some(query => query.isError);
  const error = queries.find(query => query.error)?.error;
  
  // Create a map of youtube_id to notes
  const allNotes: Record<string, Note[]> = {};
  queries.forEach((query, index) => {
    if (query.data) {
      allNotes[youtube_vods[index].youtube_id] = query.data;
    }
  });
  
  // Convert to session notes
  const sessionNotes = convertToSessionNotes(youtube_vods, allNotes);
  
  return {
    sessionNotes,
    isLoading,
    isError,
    error,
    refetch: () => {
      queries.forEach(query => query.refetch());
    }
  };
};

// Mutation to create a note
export const useCreateNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      youtubeId, 
      noteId, 
      content, 
      seconds_from_start, 
      labels 
    }: {
      youtubeId: string;
      noteId: string;
      content: string;
      seconds_from_start: number;
      labels: Record<string, string>;
    }) => {
      const response = await fetchAPI(`/notes/${youtubeId}`, {
        method: 'POST',
        body: JSON.stringify({
          note_id: noteId,
          content,
          seconds_from_start,
          labels
        })
      });
      return response;
    },
    onMutate: async ({ youtubeId, noteId, content, seconds_from_start, labels }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesKeys.youtubeId(youtubeId) });
      
      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData<Note[]>(notesKeys.youtubeId(youtubeId));
      
      // Optimistically update
      const optimisticNote: Note = {
        note_id: noteId,
        seconds_from_start,
        content,
        labels,
        user_id: 'current_user',
        version: 1
      };
      
      queryClient.setQueryData<Note[]>(
        notesKeys.youtubeId(youtubeId),
        (old) => {
          const newNotes = [...(old || []), optimisticNote];
          return sortNotes(newNotes);
        }
      );
      
      return { previousNotes, youtubeId };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousNotes) {
        queryClient.setQueryData(notesKeys.youtubeId(context.youtubeId), context.previousNotes);
      }
    },
    onSettled: (data, error, variables) => {
      // Always refetch after success or error
      queryClient.invalidateQueries({ queryKey: notesKeys.youtubeId(variables.youtubeId) });
    }
  });
};

// Mutation to update a note
export const useUpdateNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      noteId,
      content,
      seconds_from_start,
      labels,
      version
    }: {
      noteId: string;
      content: string;
      seconds_from_start: number;
      labels: Record<string, string>;
      version: number;
    }) => {
      const response = await fetchAPI(`/note/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          content,
          seconds_from_start,
          labels,
          version
        })
      });
      return response;
    },
    onMutate: async ({ noteId, content, seconds_from_start, labels, version }) => {
      // Find which youtube_id this note belongs to
      const queryCache = queryClient.getQueryCache();
      let youtubeId: string | null = null;
      let previousNotes: Note[] | undefined;
      
      // Find the query that contains this note
      for (const query of queryCache.getAll()) {
        if (query.queryKey[0] === 'notes' && query.queryKey.length === 2) {
          const notes = query.state.data as Note[] | undefined;
          if (notes?.some(note => note.note_id === noteId)) {
            youtubeId = query.queryKey[1] as string;
            previousNotes = notes;
            break;
          }
        }
      }
      
      if (!youtubeId || !previousNotes) {
        throw new Error('Could not find note to update');
      }
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesKeys.youtubeId(youtubeId) });
      
      // Optimistically update
      queryClient.setQueryData<Note[]>(
        notesKeys.youtubeId(youtubeId),
        (old) => {
          if (!old) return old;
          const updatedNotes = old.map(note => 
            note.note_id === noteId 
              ? { ...note, content, seconds_from_start, labels, version: version + 1 }
              : note
          );
          return sortNotes(updatedNotes);
        }
      );
      
      return { previousNotes, youtubeId };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousNotes && context.youtubeId) {
        queryClient.setQueryData(notesKeys.youtubeId(context.youtubeId), context.previousNotes);
      }
    },
    onSettled: (data, error, variables, context) => {
      // Always refetch after success or error
      if (context?.youtubeId) {
        queryClient.invalidateQueries({ queryKey: notesKeys.youtubeId(context.youtubeId) });
      }
    }
  });
};

// Mutation to delete a note
export const useDeleteNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (noteId: string) => {
      await fetchAPI(`/note/${noteId}`, {
        method: 'DELETE'
      });
    },
    onMutate: async (noteId) => {
      // Find which youtube_id this note belongs to
      const queryCache = queryClient.getQueryCache();
      let youtubeId: string | null = null;
      let previousNotes: Note[] | undefined;
      
      // Find the query that contains this note
      for (const query of queryCache.getAll()) {
        if (query.queryKey[0] === 'notes' && query.queryKey.length === 2) {
          const notes = query.state.data as Note[] | undefined;
          if (notes?.some(note => note.note_id === noteId)) {
            youtubeId = query.queryKey[1] as string;
            previousNotes = notes;
            break;
          }
        }
      }
      
      if (!youtubeId || !previousNotes) {
        throw new Error('Could not find note to delete');
      }
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesKeys.youtubeId(youtubeId) });
      
      // Optimistically update
      queryClient.setQueryData<Note[]>(
        notesKeys.youtubeId(youtubeId),
        (old) => {
          if (!old) return old;
          return old.filter(note => note.note_id !== noteId);
        }
      );
      
      return { previousNotes, youtubeId };
    },
    onError: (err, noteId, context) => {
      // Rollback on error
      if (context?.previousNotes && context.youtubeId) {
        queryClient.setQueryData(notesKeys.youtubeId(context.youtubeId), context.previousNotes);
      }
    },
    onSettled: (data, error, noteId, context) => {
      // Always refetch after success or error
      if (context?.youtubeId) {
        queryClient.invalidateQueries({ queryKey: notesKeys.youtubeId(context.youtubeId) });
      }
    }
  });
};

// Helper function to generate UUIDs on frontend
export const generateNoteId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}; 